import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Lock, ArrowLeft, Settings, Users, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [benchmarks, setBenchmarks] = useState({
    payroll_tax_pct: 0.10,
    benefits_pct: 0.25,
    workers_comp_pct: 0.02,
    recruiting_cost: 3000,
    onboarding_training_cost: 1500,
    vacancy_days: 14,
    vacancy_productivity_loss_pct: 0.50,
    rampup_days: 30,
    rampup_productivity_loss_pct: 0.30,
    weeks_per_year: 52
  });

  const [leads, setLeads] = useState([]);
  const [salesPersonEmail, setSalesPersonEmail] = useState('');

  useEffect(() => {
    if (authenticated) {
      fetchBenchmarks();
      fetchLeads();
    }
  }, [authenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setVerifying(true);

    try {
      const response = await axios.post(`${API}/admin/verify-password`, { password });
      
      if (response.data.valid) {
        setAuthenticated(true);
        toast.success('Access granted');
      } else {
        toast.error('Invalid password');
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error('Authentication failed');
    } finally {
      setVerifying(false);
    }
  };

  const fetchBenchmarks = async () => {
    try {
      const response = await axios.get(`${API}/benchmarks`);
      setBenchmarks(response.data);
    } catch (error) {
      console.error('Failed to fetch benchmarks:', error);
      toast.error('Failed to load benchmarks');
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  };

  const handleBenchmarkChange = (field, value) => {
    setBenchmarks(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveBenchmarks = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/benchmarks`, benchmarks);
      toast.success('Benchmarks updated successfully');
    } catch (error) {
      console.error('Failed to save benchmarks:', error);
      toast.error('Failed to save benchmarks');
    } finally {
      setLoading(false);
    }
  };

  const exportLeadsCSV = () => {
    if (leads.length === 0) {
      toast.error('No leads to export');
      return;
    }

    const headers = ['Date', 'Name', 'Company', 'Email', 'Phone', 'Savings Amount'];
    const rows = leads.map(lead => [
      new Date(lead.created_at).toLocaleDateString(),
      `${lead.first_name} ${lead.last_name}`,
      lead.company_name,
      lead.email,
      lead.phone_number,
      `$${lead.calculation_data?.savings?.amount?.toFixed(2) || 0}`
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-export-${new Date().getTime()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Leads exported successfully');
  };

  // Login Screen
  if (!authenticated) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <Toaster position="top-center" richColors />
        
        <div className="card" style={{ maxWidth: '400px', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <Lock size={48} style={{ color: '#C41E3A', marginBottom: '16px' }} />
            <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>Admin Access</h2>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>Enter password to access admin panel</p>
          </div>

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '20px' }}>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                required
                data-testid="admin-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={verifying}
              className="w-full bg-[#C41E3A] hover:bg-[#8B0000]"
              data-testid="admin-login-btn"
            >
              {verifying ? 'Verifying...' : 'Access Admin Panel'}
            </Button>
          </form>

          <button
            onClick={() => navigate('/')}
            style={{
              marginTop: '20px',
              width: '100%',
              background: 'transparent',
              color: '#6b7280',
              border: '1px solid #e5e7eb',
              padding: '10px',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#C41E3A';
              e.currentTarget.style.color = '#C41E3A';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            Back to Calculator
          </button>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%)', paddingBottom: '60px' }}>
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)', 
        padding: '20px 15px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px' }}>
            <h1 style={{ color: 'white', fontSize: 'clamp(20px, 4vw, 28px)', fontWeight: '700' }}>Admin Dashboard</h1>
            <button
              onClick={() => navigate('/')}
              style={{
                background: 'transparent',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.3)',
                padding: '10px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#C41E3A';
                e.currentTarget.style.background = 'rgba(196, 30, 58, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                e.currentTarget.style.background = 'transparent';
              }}
              data-testid="back-to-calculator-admin-btn"
            >
              <ArrowLeft size={18} />
              Back to Calculator
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 20px' }}>
        <Tabs defaultValue="benchmarks" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-[600px]">
            <TabsTrigger value="benchmarks" data-testid="benchmarks-tab">
              <Settings size={16} style={{ marginRight: '8px' }} />
              Benchmarks
            </TabsTrigger>
            <TabsTrigger value="leads" data-testid="leads-tab">
              <Users size={16} style={{ marginRight: '8px' }} />
              Leads
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">
              <Lock size={16} style={{ marginRight: '8px' }} />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Benchmarks Tab */}
          <TabsContent value="benchmarks" data-testid="benchmarks-content">
            <div className="card" style={{ marginTop: '20px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>
                Default Calculation Benchmarks
              </h3>
              
              <div style={{ display: 'grid', gap: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                  <div>
                    <Label htmlFor="payroll_tax_pct">Payroll Tax (%)</Label>
                    <Input
                      id="payroll_tax_pct"
                      type="number"
                      step="0.01"
                      value={benchmarks.payroll_tax_pct * 100}
                      onChange={(e) => handleBenchmarkChange('payroll_tax_pct', parseFloat(e.target.value) / 100)}
                      data-testid="payroll-tax-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="benefits_pct">Benefits Load (%)</Label>
                    <Input
                      id="benefits_pct"
                      type="number"
                      step="0.01"
                      value={benchmarks.benefits_pct * 100}
                      onChange={(e) => handleBenchmarkChange('benefits_pct', parseFloat(e.target.value) / 100)}
                      data-testid="benefits-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="workers_comp_pct">Workers' Comp (%)</Label>
                    <Input
                      id="workers_comp_pct"
                      type="number"
                      step="0.01"
                      value={benchmarks.workers_comp_pct * 100}
                      onChange={(e) => handleBenchmarkChange('workers_comp_pct', parseFloat(e.target.value) / 100)}
                      data-testid="workers-comp-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="recruiting_cost">Recruiting Cost ($)</Label>
                    <Input
                      id="recruiting_cost"
                      type="number"
                      value={benchmarks.recruiting_cost}
                      onChange={(e) => handleBenchmarkChange('recruiting_cost', parseFloat(e.target.value))}
                      data-testid="recruiting-cost-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="onboarding_training_cost">Onboarding/Training Cost ($)</Label>
                    <Input
                      id="onboarding_training_cost"
                      type="number"
                      value={benchmarks.onboarding_training_cost}
                      onChange={(e) => handleBenchmarkChange('onboarding_training_cost', parseFloat(e.target.value))}
                      data-testid="onboarding-cost-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="vacancy_days">Vacancy Days</Label>
                    <Input
                      id="vacancy_days"
                      type="number"
                      value={benchmarks.vacancy_days}
                      onChange={(e) => handleBenchmarkChange('vacancy_days', parseInt(e.target.value))}
                      data-testid="vacancy-days-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="vacancy_productivity_loss_pct">Vacancy Productivity Loss (%)</Label>
                    <Input
                      id="vacancy_productivity_loss_pct"
                      type="number"
                      step="0.01"
                      value={benchmarks.vacancy_productivity_loss_pct * 100}
                      onChange={(e) => handleBenchmarkChange('vacancy_productivity_loss_pct', parseFloat(e.target.value) / 100)}
                      data-testid="vacancy-loss-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="rampup_days">Ramp-up Days</Label>
                    <Input
                      id="rampup_days"
                      type="number"
                      value={benchmarks.rampup_days}
                      onChange={(e) => handleBenchmarkChange('rampup_days', parseInt(e.target.value))}
                      data-testid="rampup-days-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="rampup_productivity_loss_pct">Ramp-up Productivity Loss (%)</Label>
                    <Input
                      id="rampup_productivity_loss_pct"
                      type="number"
                      step="0.01"
                      value={benchmarks.rampup_productivity_loss_pct * 100}
                      onChange={(e) => handleBenchmarkChange('rampup_productivity_loss_pct', parseFloat(e.target.value) / 100)}
                      data-testid="rampup-loss-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="weeks_per_year">Weeks Per Year</Label>
                    <Input
                      id="weeks_per_year"
                      type="number"
                      value={benchmarks.weeks_per_year}
                      onChange={(e) => handleBenchmarkChange('weeks_per_year', parseInt(e.target.value))}
                      data-testid="weeks-per-year-input"
                    />
                  </div>
                </div>

                <Button
                  onClick={saveBenchmarks}
                  disabled={loading}
                  className="bg-[#C41E3A] hover:bg-[#8B0000] w-full sm:w-auto"
                  data-testid="save-benchmarks-btn"
                >
                  <Save size={16} style={{ marginRight: '8px' }} />
                  {loading ? 'Saving...' : 'Save Benchmarks'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads" data-testid="leads-content">
            <div className="card" style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600' }}>
                  Captured Leads ({leads.length})
                </h3>
                <Button
                  onClick={exportLeadsCSV}
                  variant="outline"
                  disabled={leads.length === 0}
                  data-testid="export-leads-btn"
                >
                  Export to CSV
                </Button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                      <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>Date</th>
                      <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>Name</th>
                      <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>Company</th>
                      <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>Email</th>
                      <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>Phone</th>
                      <th style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                          No leads captured yet
                        </td>
                      </tr>
                    ) : (
                      leads.map((lead, index) => (
                        <tr key={lead.id || index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '12px 8px', fontSize: '14px' }}>
                            {new Date(lead.created_at).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: '14px' }}>
                            {lead.first_name} {lead.last_name}
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: '14px' }}>{lead.company_name}</td>
                          <td style={{ padding: '12px 8px', fontSize: '14px' }}>{lead.email}</td>
                          <td style={{ padding: '12px 8px', fontSize: '14px' }}>{lead.phone_number}</td>
                          <td style={{ padding: '12px 8px', fontSize: '14px', fontWeight: '600', color: '#C41E3A' }}>
                            ${lead.calculation_data?.savings?.amount?.toFixed(2) || 0}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" data-testid="settings-content">
            <div className="card" style={{ marginTop: '20px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>
                Sales Person Email
              </h3>
              
              <div style={{ maxWidth: '500px' }}>
                <Label htmlFor="sales_person_email">
                  Your Email Address
                </Label>
                <Input
                  id="sales_person_email"
                  type="email"
                  value={salesPersonEmail}
                  onChange={(e) => setSalesPersonEmail(e.target.value)}
                  placeholder="salesperson@forge.com"
                  data-testid="sales-person-email-settings-input"
                />
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>
                  This email will receive notifications when new leads are captured. You can also enter this in the calculator form.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
